import { useRef } from "react";
import QrStamp from "./QrStamp";
import { toTitleCase } from "@/lib/utils";
import { veineOnyx } from "@/lib/onyx";

const REDUCE =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * The passport as a precious object: an onyx card, veined for its own
 * dossier (RDEC-025), whose gold sheen follows the pointer with a slight 3D
 * tilt. The
 * pointer position feeds two CSS variables; all motion lives in CSS
 * (.passport-card / .passport-card-sheen). Touch screens get a slow idle
 * sheen drift instead (.passport-idle-sheen); reduced-motion gets a static
 * card.
 */
export default function PassportCard({ data, t, qrSrc }) {
  const ref = useRef(null);

  const setPointer = (px, py) => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--px", px);
    el.style.setProperty("--py", py);
  };
  const onMove = (e) => {
    if (REDUCE) return;
    const r = e.currentTarget.getBoundingClientRect();
    setPointer(
      (e.clientX - r.left) / r.width,
      (e.clientY - r.top) / r.height
    );
  };
  const onLeave = () => setPointer(0.5, 0.5);

  return (
    <article
      ref={ref}
      data-testid="passport-card"
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      style={{ "--onyx-veine": veineOnyx(data.provena_id) }}
      className="passport-card zone-graphite passport-onyx relative w-full max-w-xl mx-auto overflow-hidden border border-gold/45 aspect-[8/5] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-[7px] border border-gold/20"
      />
      <img
        src="/provena-logo.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none select-none absolute -right-8 -bottom-10 w-1/2 opacity-[0.05]"
        draggable="false"
      />
      {!REDUCE && (
        <div
          aria-hidden="true"
          className="passport-card-sheen pointer-events-none absolute inset-0 mix-blend-screen"
        />
      )}
      <div
        aria-hidden="true"
        className="passport-idle-sheen pointer-events-none absolute inset-0 mix-blend-screen"
      />

      <div className="relative z-10 flex h-full flex-col justify-between p-5 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          {/* Un document, pas un acte scellé : ni guilloché ni médaillon de
              métal sur le passeport (RDEC-016, RDEC-017). */}
          <div className="flex items-center gap-3">
            <div>
              <p className="font-serif text-lg sm:text-xl tracking-[0.18em] text-ink-soft">
                PROVENA
              </p>
              <p className="text-[9px] sm:text-[10px] tracking-[0.3em] uppercase text-gold-ink">
                {t("passport.heroEyebrow")}
              </p>
            </div>
          </div>
          {data.year && (
            <p className="text-[9px] sm:text-[10px] tracking-[0.25em] uppercase text-ink-subtle">
              {data.year}
            </p>
          )}
        </div>

        <div>
          <h2 className="font-serif text-xl sm:text-2xl leading-tight">
            {data.brand}
          </h2>
          <p className="font-serif text-sm sm:text-base text-ink-muted">
            {data.model}
          </p>
          {data.owner_name && (
            <p className="mt-1.5 text-[9px] sm:text-[10px] tracking-[0.25em] uppercase text-ink-subtle">
              {t("passport.cardHolder")} ·{" "}
              <span className="normal-case tracking-normal text-ink-soft">
                {toTitleCase(data.owner_name)}
              </span>
            </p>
          )}
        </div>

        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="mb-1 text-[11px] tracking-[0.2em] uppercase text-ink-muted">
              {t("passport.edition")}
            </p>
            {/* Un identifiant se lit, se compare et se saisit : mono, chiffres
                tabulaires, couleur unie (RDEC-017). */}
            <p
              data-testid="passport-card-id"
              className="font-mono tabular-nums text-base sm:text-xl tracking-[0.12em] text-gold-ink"
            >
              {data.provena_id}
            </p>
          </div>
          <div className="shrink-0 rotate-[-2deg] border border-gold/40 bg-surface p-1.5">
            <QrStamp
              src={qrSrc}
              alt={t("passport.qrAlt")}
              size={56}
              testId="passport-card-qr"
            />
          </div>
        </div>
      </div>
    </article>
  );
}
